import React, { useMemo, useState } from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import { Pager } from "../DataIntegration/shared";
import {
  AlertTriangleIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  DownloadIcon,
  EditIcon,
  FilterIcon,
  LockIcon,
  MailIcon,
  MoreIcon,
  PlusIcon,
  SearchIcon,
  ShieldCheckIcon,
  UserIcon,
  UsersIcon,
} from "../../Components/Icons";
import {
  ACCESS_CHANGES,
  DEPARTMENTS,
  ROLES,
  ROLE_BREAKDOWN,
  ROLE_NAMES,
  TOTAL_USERS,
  USERS,
  USER_STATS,
  USER_STATUSES,
} from "./data";
import "./UserManagement.css";

const STAT_ICONS = {
  users: UsersIcon,
  check: CheckCircleIcon,
  mail: MailIcon,
  shield: ShieldCheckIcon,
  lock: LockIcon,
};

const CHANGE_ICONS = {
  info: ShieldCheckIcon,
  critical: LockIcon,
  warning: MailIcon,
  muted: AlertTriangleIcon,
};

const chipClass = (value) =>
  `chip chip--${value.toLowerCase().replace(/[\s/&]+/g, "-")}`;

/** Two initials for the avatar, so the list scans without photos. */
const initials = (name) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

export default function UserManagement() {
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [department, setDepartment] = useState("");
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return USERS.filter((user) => {
      if (term && !`${user.name} ${user.email}`.toLowerCase().includes(term)) {
        return false;
      }
      if (role && user.role !== role) return false;
      if (department && user.department !== department) return false;
      if (status && user.status !== status) return false;
      return true;
    });
  }, [search, role, department, status]);

  const resetPage = () => setPage(1);

  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">User Management</h1>
          <p className="page__subtitle">
            Manage user accounts, roles and access across the DGI Smart Tax
            Registry.
          </p>
        </div>
        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <DownloadIcon size={17} />
            Export Users
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <PlusIcon size={17} />
            Add User
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------- stats -- */}
      <section className="row g-3" aria-label="User summary">
        {USER_STATS.map((card) => {
          const Icon = STAT_ICONS[card.icon];
          return (
            <div className="col-6 col-lg-4 col-xxl" key={card.key}>
              <article className="di-stat h-100">
                <span className={`kpi__icon kpi__icon--${card.tone}`}>
                  <Icon size={22} />
                </span>
                <div className="di-stat__meta">
                  <p className="kpi__label">{card.label}</p>
                  <p className="di-stat__value">{card.value}</p>
                  <p
                    className={`di-stat__foot${
                      card.footTone ? ` di-stat__foot--${card.footTone}` : ""
                    }`}
                  >
                    {card.foot}
                  </p>
                </div>
              </article>
            </div>
          );
        })}
      </section>

      {/* -------------------------------------------------- users + side -- */}
      <div className="um-main">
        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Users</h2>
          </div>

          <div className="di-filters">
            <div className="filters__search">
              <input
                type="search"
                placeholder="Search by name or email..."
                aria-label="Search users"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  resetPage();
                }}
              />
              <SearchIcon size={17} />
            </div>

            {[
              { value: role, set: setRole, label: "All Roles", options: ROLE_NAMES },
              {
                value: department,
                set: setDepartment,
                label: "All Departments",
                options: DEPARTMENTS,
              },
              {
                value: status,
                set: setStatus,
                label: "All Statuses",
                options: USER_STATUSES,
              },
            ].map((filter) => (
              <span className="select select--block" key={filter.label}>
                <select
                  value={filter.value}
                  aria-label={filter.label}
                  onChange={(event) => {
                    filter.set(event.target.value);
                    resetPage();
                  }}
                >
                  <option value="">{filter.label}</option>
                  {filter.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={16} />
              </span>
            ))}

            <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
              <FilterIcon size={16} />
              Filters
            </button>
          </div>

          <div className="dgi-table-wrap">
            <table className="dgi-table dgi-table--users">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Last Sign-in</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <span className="um-user">
                        <span className="um-user__avatar">
                          {initials(user.name)}
                        </span>
                        <span className="um-user__text">
                          <button type="button" className="link">
                            {user.name}
                          </button>
                          <span className="dgi-table__sub">{user.email}</span>
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className={chipClass(user.role)}>{user.role}</span>
                    </td>
                    <td>{user.department}</td>
                    <td>
                      <span
                        className={`chip chip--dot ${chipClass(user.status)}`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td>
                      {user.lastDate}
                      <span className="dgi-table__sub">{user.lastTime}</span>
                    </td>
                    <td>
                      <span className="row-actions">
                        <button
                          type="button"
                          className="row-actions__btn"
                          aria-label={`Edit ${user.name}`}
                        >
                          <EditIcon size={15} />
                        </button>
                        <button
                          type="button"
                          className="dgi-card__more"
                          aria-label={`Actions for ${user.name}`}
                        >
                          <MoreIcon size={18} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <p className="dgi-table__empty">No users match these filters.</p>
          )}

          <Pager
            page={page}
            setPage={setPage}
            perPage={perPage}
            setPerPage={setPerPage}
            total={TOTAL_USERS}
            shown={rows.length}
            noun="users"
          />
        </section>

        <div className="stack">
          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Users by Role</h2>
            </div>
            <DonutChart
              segments={ROLE_BREAKDOWN}
              total={TOTAL_USERS}
              totalLabel="Total Users"
            />
            <button type="button" className="dgi-card__link">
              View all roles <ArrowRightIcon size={16} />
            </button>
          </section>

          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Recent Access Changes</h2>
            </div>
            <ul className="changes">
              {ACCESS_CHANGES.map((change) => {
                const Icon = CHANGE_ICONS[change.tone];
                return (
                  <li className="change" key={change.id}>
                    <span className={`change__icon change__icon--${change.tone}`}>
                      <Icon size={16} />
                    </span>
                    <div>
                      <p className="change__title">{change.title}</p>
                      <p className="change__meta">
                        {change.who} · by {change.by}
                      </p>
                      <p className="change__when">{change.when}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <button type="button" className="dgi-card__link">
              View access history <ArrowRightIcon size={16} />
            </button>
          </section>
        </div>
      </div>

      {/* ------------------------------------------ roles & permissions -- */}
      <section className="dgi-card">
        <div className="dgi-card__header">
          <h2 className="dgi-card__title">Roles &amp; Permissions</h2>
          <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
            <PlusIcon size={16} />
            Add Role
          </button>
        </div>
        <div className="dgi-table-wrap">
          <table className="dgi-table dgi-table--roles">
            <thead>
              <tr>
                <th>Role</th>
                <th>Description</th>
                <th>Users</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ROLES.map((item) => (
                <tr key={item.key}>
                  <td>
                    <span className="um-role">
                      <span className="um-role__icon">
                        <UserIcon size={15} />
                      </span>
                      {item.name}
                    </span>
                  </td>
                  <td className="um-desc">{item.description}</td>
                  <td>{item.users}</td>
                  <td>{item.permissions}</td>
                  <td>
                    <span className="row-actions">
                      <button
                        type="button"
                        className="row-actions__btn"
                        aria-label={`Edit ${item.name}`}
                      >
                        <EditIcon size={15} />
                      </button>
                      <button
                        type="button"
                        className="dgi-card__more"
                        aria-label={`Actions for ${item.name}`}
                      >
                        <MoreIcon size={18} />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
